/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import {
  Form,
  Input,
  InputNumber,
  Button,
  Upload,
  App,
  Space,
  Typography,
  Spin
} from 'antd'
import { ImagePlus } from 'lucide-react'
import { useFFEStore } from '../../store/ffeStore'
import type { Material } from '../../types/ffe'
import { useApi } from '../../services/ApiContext'
import { SupplierInput } from '../LineItem/LineItemDetailsDrawer/SupplierInput'
import TextArea from 'antd/es/input/TextArea'

const { Text } = Typography

interface Props {
  initialValues?: Partial<Material>
  onSuccess?: () => void
}

export const AccessoriesForm: React.FC<Props> = ({
  initialValues,
  onSuccess
}) => {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { addMaterial, updateMaterial } = useFFEStore()
  const defaultMargin = useFFEStore(
    (state) => state.document.settings.defaultMargin
  )
  const [imageUrl, setImageUrl] = React.useState<string | undefined>(
    initialValues?.image?.url
  )
  const [loading, setLoading] = React.useState(false)

  const api = useApi()

  const handleImageUpload = async ({ file }: { file: File }) => {
    try {
      setLoading(true)
      if (!(file instanceof File)) {
        throw new Error('Invalid file')
      }

      const objectUrl = await api.uploadImage(file)
      setImageUrl(objectUrl)
      message.success('Image uploaded successfully')
    } catch (error) {
      console.error(error)
      message.error('Failed to upload image')
    }
    setLoading(false)
  }

  const handleSubmit = (values: any) => {
    const materialData: Omit<Material, 'id'> = {
      name: values.name,
      materialType: 'accessory',
      type: values.type,
      supplier: values.supplier,
      supplierItemCode: values.supplierItemCode,
      leadTime: values.leadTime,
      costPerUnit: values.costPerUnit,
      sellPricePerUnit: values.sellPricePerUnit,
      unit: values.unit,
      margin:
        ((values.sellPricePerUnit - values.costPerUnit) / values.costPerUnit) *
        100,
      ...(imageUrl && {
        image: {
          url: imageUrl,
          alt: values.name
        }
      })
    }

    if (initialValues && initialValues.id) {
      updateMaterial(initialValues.id, materialData)
      message.success('Finish updated successfully')
    } else {
      addMaterial(materialData)
      message.success('Finish added successfully')
    }

    form.resetFields()
    setImageUrl(undefined)
    onSuccess?.()
  }

  if (!initialValues || initialValues.margin === undefined) {
    initialValues = {
      ...initialValues,
      margin: defaultMargin
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <div className="flex gap-4 mb-6">
        <Spin spinning={loading}>
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={handleImageUpload}
            className="flex-shrink-0"
          >
            <div className="w-[100px] h-[100px] flex items-center justify-center border border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Material preview"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="text-center">
                  <ImagePlus className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                  <Text type="secondary" className="text-sm">
                    Upload Image
                  </Text>
                </div>
              )}
            </div>
          </Upload>
        </Spin>

        <Space direction="vertical" className="flex-1">
          <Form.Item
            name="name"
            label="Accessory Name"
            rules={[{ required: true, message: 'Add accessory name' }]}
          >
            <Input placeholder="e.g., Monitor, Desk Stand" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Accessory Description"
            rules={[{ required: false, message: 'Add accessory description' }]}
          >
            <TextArea placeholder="Add accessory description" />
          </Form.Item>
        </Space>
      </div>

      <Space className="w-full flex flex-col md:flex-row items-stretch md:items-center">
        <Form.Item name="supplier" label="Supplier">
          <SupplierInput />
        </Form.Item>
        <Form.Item
          name="supplierItemCode"
          label="Supplier Item Code"
          className="flex-1"
        >
          <Input placeholder="Enter supplier's code" />
        </Form.Item>

        <Form.Item name="leadTime" label="Lead Time" className="flex-1">
          <Input placeholder="e.g., 2-3 weeks" />
        </Form.Item>
      </Space>

      <Space className="w-full flex flex-col md:flex-row items-stretch md:items-center">
        <Form.Item
          name="costPerUnit"
          label="Cost Price"
          rules={[{ required: true, message: 'Please enter cost price' }]}
          className="flex-1"
        >
          <InputNumber
            prefix="$"
            min={0}
            precision={2}
            className="w-full"
            placeholder="0.00"
            onChange={(value) => {
              if (value) {
                form.setFieldsValue({
                  sellPricePerUnit:
                    value + (value * form.getFieldValue('margin')) / 100
                })
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="margin"
          label="Margin"
          rules={[{ required: true, message: 'Please enter margin' }]}
          className="flex-1"
        >
          <InputNumber
            suffix="%"
            min={0}
            precision={2}
            className="w-full"
            placeholder="0.0"
            onChange={(value) => {
              if (value) {
                form.setFieldsValue({
                  sellPricePerUnit:
                    form.getFieldValue('costPerUnit') +
                    (form.getFieldValue('costPerUnit') * value) / 100
                })
              }
            }}
          />
        </Form.Item>
        <Form.Item
          name="sellPricePerUnit"
          label="Sell Price"
          rules={[{ required: true, message: 'Please enter sell price' }]}
          className="flex-1"
        >
          <InputNumber
            prefix="$"
            min={0}
            precision={2}
            className="w-full"
            placeholder="0.00"
            onChange={(value) => {
              if (value) {
                form.setFieldsValue({
                  margin:
                    ((value - form.getFieldValue('costPerUnit')) /
                      form.getFieldValue('costPerUnit')) *
                    100
                })
              }
            }}
          />
        </Form.Item>
      </Space>

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          {initialValues?.id ? 'Update Accessory' : 'Add Accessory'}
        </Button>
      </Form.Item>
    </Form>
  )
}
